import React, { useState, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useVehicleProfile, useVehicles, useVehicleTypes } from "../../hooks/useVehicleQuery";
import { useOperations } from "../../hooks/useDocumentationVehicleQuery";
import LoadingAnimation from "../../components/loading/LoadingAnimation";
import { useLayout } from "../../context/LayoutContext";
import VehicleDocumentCard from "../../components/ui/cards/VehicleDocumentCard";
import VehicleDocumentRow from "../../components/ui/cards/VehicleDocumentRow";
import PreviewModal from "../../components/ui/modals/PreviewModal";
import _ from 'lodash';

// Iconos para el cambio de vista
import GridViewIcon from "../../assets/svg/view/grid.svg";
import GridViewLightIcon from "../../assets/svg/view/grid_light.svg";
import ListViewIcon from "../../assets/svg/view/list.svg";
import ListViewLightIcon from "../../assets/svg/view/list_light.svg";

import "../../assets/scss/views/DashboardVehicleDocuments.scss";

// Función para calcular el estado de un documento
const calculateDocumentStatus = (document, isRequired = false) => {
  if (!document.file_name && !document.id?.startsWith?.('temp-')) {
    return isRequired ? "missing-required" : "missing-optional";
  }
  
  if (!isRequired) return "not-required";
  if (!document.expiration_date) return "empty";

  const today = new Date();
  const expDate = new Date(document.expiration_date);
  const daysUntilExpiration = Math.ceil(
    (expDate - today) / (1000 * 60 * 60 * 24)
  );

  if (daysUntilExpiration < 0) return "expired";
  if (daysUntilExpiration <= 15) return "critical";
  if (daysUntilExpiration <= 30) return "warning";
  return "valid";
};

export default function DashboardVehicleDocuments() {
  const { theme } = useLayout();
  const navigate = useNavigate();

  // Estados principales con arquitectura de separación de responsabilidades
  const [selectedPage, setSelectedPage] = useState(1);
  const [vehiclesPerPage] = useState(20);
  
  // Estado para el tipo de vista (cards o linear)
  const [viewType, setViewType] = useState("cards");
  
  // Estados de búsqueda: Separación entre UI y logica de negocio
  const [searchInputValue, setSearchInputValue] = useState("");
  const [activeSearchFilter, setActiveSearchFilter] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  
  const [sortOption, setSortOption] = useState("");
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [selectedVehicleType, setSelectedVehicleType] = useState("main");
  
  // Estados para filtros adicionales
  const [vehicleTypeFilter, setVehicleTypeFilter] = useState("");
  const [operationFilter, setOperationFilter] = useState("");
  const [documentFilter, setDocumentFilter] = useState("all");

  const [showExtraDocuments, setShowExtraDocuments] = useState(false);

  // Estados para modales
  const [openPreview, setOpenPreview] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState(null);

  // Implementación de debounce con Lodash - Patrón de optimización avanzada
  const debouncedSearch = useCallback(
    _.debounce((searchValue) => {
      setActiveSearchFilter(searchValue);
      setIsSearching(false);
      console.log('Consulta ejecutada con:', searchValue);
    }, 900), // 300ms para mejor responsividad
    []
  );

  // Hook principal para obtener vehículos con filtro optimizado
  const { data: dataVehicles, isLoading: isLoadingVehicles } = useVehicles(
    selectedPage,
    vehiclesPerPage,
    activeSearchFilter, // Usar el filtro activo, no el valor del input
    sortOption
  );

  // Hook para obtener perfil del vehículo seleccionado
  const {
    vehicleData: selectedVehicleData,
    isLoading: isLoadingVehicleProfile,
  } = useVehicleProfile(selectedVehicle?.id || null, {
    enabled: !!selectedVehicle,
    staleTime: 5 * 60 * 1000,
  });

  // Hooks para filtros con gestión de estado centralizada
  const { data: vehicleTypesData, isLoading: isLoadingVehicleTypes } = useVehicleTypes({
    active: true,
    limit: 100,
  });
  const vehicleTypes = vehicleTypesData?.data || [];

  const { operations, isLoading: isLoadingOperations } = useOperations({
    limit: 100,
  });

  // Procesamiento de datos con memoización optimizada
  const allVehicles = useMemo(() => {
    if (!dataVehicles) return [];
    
    const normalVehicles = (dataVehicles.normalVehicles?.vehicles || []).map(vehicle => ({
      ...vehicle,
      vehicleType: 'normal',
      displayPlate: vehicle.plate,
      isMainVehicle: true
    }));

    const combinedVehicles = (dataVehicles.combinedVehicles?.vehicles || []).map(combined => ({
      id: combined.vehicle_id,
      plate: combined.vehicle_plate,
      vehicle_type_name: combined.vehicle_type_name,
      operation_name: combined.operation_name,
      owner_first_name: combined.owner_first_name,
      owner_last_name: combined.owner_last_name,
      operator_first_name: combined.operator_first_name,
      operator_last_name: combined.operator_last_name,
      vehicleType: 'combined',
      displayPlate: `${combined.vehicle_plate} + ${combined.trailer_plate}`,
      trailer_plate: combined.trailer_plate,
      trailer_type_name: combined.trailer_type_name,
      isMainVehicle: false,
      is_active: combined.is_active
    }));

    return [...normalVehicles, ...combinedVehicles];
  }, [dataVehicles]);

  // Sistema de filtrado multicapa con rendimiento optimizado
  const filteredVehicles = useMemo(() => {
    return allVehicles.filter(vehicle => {
      if (vehicleTypeFilter && vehicle.vehicle_type_name !== vehicleTypeFilter) {
        return false;
      }
      if (operationFilter && vehicle.operation_name !== operationFilter) {
        return false;
      }
      return true;
    });
  }, [allVehicles, vehicleTypeFilter, operationFilter]);

  const getCombinedDocuments = useMemo(() => {
    if (!selectedVehicleData) return [];

    let requiredDocuments = [];
    let existingDocuments = [];

    if (selectedVehicleType === "main") {
      requiredDocuments = selectedVehicleData.requiredDocuments || [];
      existingDocuments = selectedVehicleData.documents || [];
    } else if (selectedVehicleType === "related" && selectedVehicleData.relatedVehicle) {
      requiredDocuments = selectedVehicleData.requiredDocumentsRelatedVehicle || [];
      existingDocuments = selectedVehicleData.relatedVehicle.data.documents || [];
    }

    // Algoritmo de mapeo eficiente para gestión de documentos
    const existingDocsByType = new Map();
    existingDocuments.forEach(doc => {
      const typeId = doc.document_type_id;
      if (!existingDocsByType.has(typeId)) {
        existingDocsByType.set(typeId, []);
      }
      existingDocsByType.get(typeId).push(doc);
    });

    const combinedDocuments = [];

    // Procesamiento de documentos requeridos con lógica de negocio avanzada
    requiredDocuments.forEach(requiredType => {
      const existingDocsForType = existingDocsByType.get(requiredType.id) || [];
      
      if (existingDocsForType.length > 0) {
        existingDocsForType.forEach(doc => {
          combinedDocuments.push({
            ...doc,
            isRequired: true,
            requiredDocumentInfo: requiredType,
            isMissing: false
          });
        });
      } else {
        combinedDocuments.push({
          id: `missing-${requiredType.id}`,
          document_type_id: requiredType.id,
          document_type_name: requiredType.name,
          file_name: null,
          file_path: null,
          expedition_date: null,
          expiration_date: null,
          original_name_description: requiredType.name,
          required: true,
          isRequired: true,
          isMissing: true,
          requiredDocumentInfo: requiredType
        });
      }
      
      existingDocsByType.delete(requiredType.id);
    });

    // Integración de documentos adicionales
    existingDocsByType.forEach((docs) => {
      docs.forEach(doc => {
        combinedDocuments.push({
          ...doc,
          isRequired: false,
          isMissing: false,
          isExtraDocument: true
        });
      });
    });

    return combinedDocuments;
  }, [selectedVehicleData, selectedVehicleType]);

  const hasExtraDocuments = useMemo(() => {
    return getCombinedDocuments.some(doc => doc.isExtraDocument);
  }, [getCombinedDocuments]);

  const extraDocumentsCount = useMemo(() => {
    return getCombinedDocuments.filter(doc => doc.isExtraDocument).length;
  }, [getCombinedDocuments]);

  const filteredDocuments = useMemo(() => {
    let documentsToFilter = getCombinedDocuments;

    if (showExtraDocuments) {
      documentsToFilter = documentsToFilter.filter(doc => doc.isExtraDocument);
    } else {
      documentsToFilter = documentsToFilter.filter(doc => doc.isRequired || doc.isMissing);
    }

    return documentsToFilter.filter(doc => {
      const status = calculateDocumentStatus(doc, doc.isRequired);

      switch (documentFilter) {
        case "expired":
          return status === "expired";
        case "critical":
          return status === "critical";
        case "warning":
          return status === "warning";
        case "empty":
          return status === "empty" || status === "missing-required" || status === "missing-optional";
        case "required":
          return doc.isRequired;
        case "not-required":
          return !doc.isRequired;
        default:
          return true;
      }
    });
  }, [getCombinedDocuments, documentFilter, showExtraDocuments]);

  // Handlers con patrón de gestión de eventos optimizado
  const handleToggleExtraDocuments = useCallback((checked) => {
    setShowExtraDocuments(checked);
    setDocumentFilter("all");
  }, []);

  const handleVehicleSelect = useCallback((vehicle) => {
    setSelectedVehicle(vehicle);
    setSelectedVehicleType("main");
    setShowExtraDocuments(false);
    setDocumentFilter("all");
  }, []);

  const handleVehicleTypeChange = useCallback((type) => {
    setSelectedVehicleType(type);
    setShowExtraDocuments(false);
    setDocumentFilter("all");
  }, []);

  // Handler de búsqueda optimizado con Lodash
  const handleSearchChange = useCallback((e) => {
    const value = e.target.value;
    setSearchInputValue(value); // Actualización inmediata del UI
    setIsSearching(true); // Activar indicador de búsqueda
    debouncedSearch(value); // Ejecutar búsqueda debouncada
  }, [debouncedSearch]);

  const handlePreviewDocument = useCallback((doc) => {
    if (!doc.isMissing && doc.file_name) {
      setSelectedDoc(doc);
      setOpenPreview(true);
    }
  }, []);

  const handleEditDocument = useCallback((vehicleId) => {
    navigate(`/vehicles/${vehicleId}`);
  }, [navigate]);

  // Handler para limpiar búsqueda con reseteo completo
  const handleClearSearch = useCallback(() => {
    setSearchInputValue("");
    setActiveSearchFilter("");
    setIsSearching(false);
    debouncedSearch.cancel(); // Cancelar búsquedas pendientes
  }, [debouncedSearch]);

  // Handler para cambiar tipo de vista
  const handleViewTypeChange = useCallback(() => {
    setViewType(prevType => prevType === "cards" ? "linear" : "cards");
  }, []);

  // Función para renderizar el icono según el tema
  const renderViewIcon = () => {
    if (viewType === "cards") {
      return theme === "dark" ? ListViewLightIcon : ListViewIcon;
    } else {
      return theme === "dark" ? GridViewLightIcon : GridViewIcon;
    }
  };

  // Lógica de negocio para vehículos relacionados
  const hasRelatedVehicle = useMemo(() => {
    return selectedVehicleData?.relatedVehicle && selectedVehicleData.relatedVehicle.data;
  }, [selectedVehicleData]);

  const getRelatedVehicleTypeLabel = useMemo(() => {
    if (!selectedVehicleData || !hasRelatedVehicle) return "";
    return selectedVehicleData.relatedVehicle.type === "trailer" ? "Trailer" : "Cabezote";
  }, [selectedVehicleData, hasRelatedVehicle]);

  // Gestión de estados de carga
  if (isLoadingVehicles) {
    return (
      <div className={`dashboard-loading theme-${theme}`}>
        <LoadingAnimation />
      </div>
    );
  }

  return (
    <div className={`dashboard-vehicle-docs-container theme-${theme}`}>
      {/* Sistema de filtros con arquitectura modular */}
      <div className={`controls-section theme-${theme}`}>
        <div className="filters-container">
          <div className="filter-group">
            <div className="search-input-container">
              <input
                type="text"
                placeholder="Buscar por placa..."
                value={searchInputValue} // Input controlado por estado inmediato
                onChange={handleSearchChange}
                className={`search-input theme-${theme} ${isSearching ? 'searching' : ''}`}
              />
              {searchInputValue && (
                <button
                  type="button"
                  onClick={handleClearSearch}
                  className={`clear-search-btn theme-${theme}`}
                  aria-label="Limpiar búsqueda"
                >
                  ×
                </button>
              )}
              {isSearching && (
                <div className={`search-loading-indicator theme-${theme}`}>
                  <span>Buscando...</span>
                </div>
              )}
            </div>
          </div>

          <div className="filter-group">
            <select
              onChange={(e) => setVehicleTypeFilter(e.target.value)}
              value={vehicleTypeFilter}
              className={`select-input theme-${theme}`}
              disabled={isLoadingVehicleTypes}
            >
              <option value="">Todos los tipos</option>
              {vehicleTypes.map((type) => (
                <option key={type.id} value={type.name}>
                  {type.name}
                </option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <select
              onChange={(e) => setOperationFilter(e.target.value)}
              value={operationFilter}
              className={`select-input theme-${theme}`}
              disabled={isLoadingOperations}
            >
              <option value="">Todas las operaciones</option>
              {operations.map((operation) => (
                <option key={operation.id} value={operation.name}>
                  {operation.name}
                </option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <select
              onChange={(e) => setSortOption(e.target.value)}
              value={sortOption}
              className={`select-input theme-${theme}`}
            >
              <option value="">Ordenar por</option>
              <option value="placa_asc">Placa A-Z</option>
              <option value="placa_desc">Placa Z-A</option>
              <option value="activos">Solo activos</option>
              <option value="inactivos">Solo inactivos</option>
            </select>
          </div>
        </div>
      </div>

      {/* Layout principal con arquitectura de componentes */}
      <div className={`main-layout theme-${theme}`}>
        {/* Panel de vehículos con indicadores de estado avanzados */}
        <div className={`vehicles-panel theme-${theme}`}>
          <div className="panel-header">
            <h3>
              Vehículos ({filteredVehicles.length})
              {activeSearchFilter && (
                <span className="search-indicator">
                  - Filtrado por: "{activeSearchFilter}"
                </span>
              )}
              {isSearching && (
                <span className="search-status">
                  - Buscando...
                </span>
              )}
            </h3>
          </div>
          
          <div className="vehicles-list">
            {filteredVehicles.map((vehicle) => (
              <div
                key={`${vehicle.id}-${vehicle.vehicleType}`}
                className={`vehicle-item theme-${theme} ${
                  selectedVehicle?.id === vehicle.id ? "selected" : ""
                }`}
                onClick={() => handleVehicleSelect(vehicle)}
              >
                <div className="vehicle-info">
                  <div className="vehicle-plate">{vehicle.displayPlate}</div>
                  <div className="vehicle-type">{vehicle.vehicle_type_name}</div>
                  <div className="vehicle-operation">
                    {vehicle.operation_name || "Sin operación"}
                  </div>
                  {vehicle.vehicleType === 'combined' && (
                    <div className="vehicle-badge">Combinado</div>
                  )}
                </div>
                <div className={`vehicle-status ${vehicle.is_active ? 'active' : 'inactive'}`}>
                  {vehicle.is_active ? 'Activo' : 'Inactivo'}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Panel de documentos con gestión de estado compleja */}
        <div className={`documents-panel theme-${theme}`}>
          {selectedVehicle ? (
            <>
              <div className="panel-header">
                <h3>
                  Documentos de {selectedVehicle.displayPlate}
                </h3>
                
                <div className="header-actions">
                  {/* Botón de cambio de vista */}
                  <button
                    className={`view-toggle-btn theme-${theme}`}
                    onClick={handleViewTypeChange}
                    title={viewType === "cards" ? "Vista de lista" : "Vista de tarjetas"}
                  >
                    <img 
                      src={renderViewIcon()} 
                      alt={viewType === "cards" ? "Lista" : "Tarjetas"}
                    />
                  </button>

                  {hasRelatedVehicle && (
                    <div className={`GM__${theme}-nav-group`}>
                      <div className={`GM__${theme}-button-group`}>
                        <button
                          className={`GM__${theme}-nav-button ${
                            selectedVehicleType === "main" ? "active" : ""
                          } left`}
                          onClick={() => handleVehicleTypeChange("main")}
                        >
                          Documentos{" "}
                          <strong>
                            {selectedVehicleData?.is_trailer ? "Trailer" : "Cabezote"}
                          </strong>
                        </button>
                        <button
                          className={`GM__${theme}-nav-button ${
                            selectedVehicleType === "related" ? "active" : ""
                          } right`}
                          onClick={() => handleVehicleTypeChange("related")}
                        >
                          Documentos{" "}
                          <strong>{getRelatedVehicleTypeLabel}</strong>
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="document-filters">
                <div className="filters-row">
                  <select
                    onChange={(e) => setDocumentFilter(e.target.value)}
                    value={documentFilter}
                    className={`select-input theme-${theme}`}
                  >
                    <option value="all">Todos los documentos</option>
                    <option value="expired">Vencidos</option>
                    <option value="critical">Críticos (≤15 días)</option>
                    <option value="warning">Próximos a vencer (≤30 días)</option>
                    <option value="empty">Sin archivos / Faltantes</option>
                    <option value="required">Requeridos</option>
                    <option value="not-required">No requeridos</option>
                  </select>

                  {hasExtraDocuments && (
                    <div className={`extra-docs-toggle theme-${theme}`}>
                      <label className="toggle-container">
                        <input
                          type="checkbox"
                          checked={showExtraDocuments}
                          onChange={(e) => handleToggleExtraDocuments(e.target.checked)}
                          className="toggle-input"
                        />
                        <span className="toggle-slider"></span>
                        <span className="toggle-text">
                          {showExtraDocuments ? 'Documentos Extra' : 'Documentos Requeridos'}
                          <span className="toggle-count">
                            ({showExtraDocuments ? extraDocumentsCount : getCombinedDocuments.filter(d => d.isRequired).length})
                          </span>
                        </span>
                      </label>
                    </div>
                  )}
                </div>
              </div>

              {isLoadingVehicleProfile ? (
                <div className="loading-documents">
                  <LoadingAnimation />
                </div>
              ) : (
                <div className={viewType === "cards" ? "documents-grid" : "documents-list"}>
                  {filteredDocuments.length > 0 ? (
                    filteredDocuments.map((doc) => {
                      const commonProps = {
                        key: doc.id,
                        document: doc,
                        theme: theme,
                        onPreview: () => handlePreviewDocument(doc),
                        onEdit: () => handleEditDocument(
                          selectedVehicleType === "main" 
                            ? selectedVehicle.id 
                            : selectedVehicleData.relatedVehicle.data.id
                        ),
                        status: calculateDocumentStatus(doc, doc.isRequired),
                        isRequired: doc.isRequired,
                        isMissing: doc.isMissing,
                        isExtraDocument: doc.isExtraDocument
                      };

                      return viewType === "cards" 
                        ? <VehicleDocumentCard {...commonProps} />
                        : <VehicleDocumentRow {...commonProps} />;
                    })
                  ) : (
                    <div className="no-documents">
                      <p>
                        {showExtraDocuments 
                          ? "No hay documentos extra para mostrar" 
                          : "No hay documentos que coincidan con los filtros seleccionados"
                        }
                      </p>
                    </div>
                  )}
                </div>
              )}
            </>
          ) : (
            <div className="no-vehicle-selected">
              <h3>Selecciona un vehículo</h3>
              <p>Elige un vehículo de la lista para ver sus documentos</p>
            </div>
          )}
        </div>
      </div>

      <PreviewModal
        open={openPreview}
        onClose={() => {
          setOpenPreview(false);
          setSelectedDoc(null);
        }}
        doc={selectedDoc}
        type="vehicle"
      />
    </div>
  );
}